// @migration-in-progress
/**
 * Vessel Requirements System - Business Logic + CSV Data
 *
 * THIS IS THE SOURCE OF TRUTH for all vessel requirements logic.
 * Requirements data is now generated from CSV files at build time.
 *
 * To update requirements:
 * 1. Modify CSV files in /public/reference-data/archive/
 * 2. Run: npm run update-requirements
 * 3. The generated data will be imported here
 *
 * Structure:
 * - Basic requirements (apply to ALL vessels)
 * - Conditional requirements (based on vessel configuration)
 *
 * @see parseCategory for category format handling ('cat3', 3, '3', 'CAT3', 'IWW')
 * - Area-specific requirements (based on operating area)
 * - Vessel type-specific requirements
 */

// Import generated requirements from CSV data
import { getGeneratedVesselRequirements, seagoingRequirements, inlandRequirements } from './vesselRequirements-generated';
// Import requirements from domain packs (same source as FFE, LSA, etc.)
import { getRequirementsFromDomains } from './vesselRequirements-from-domains';
import { parseCategory } from '../utils/categoryHelpers';

/**
 * Check if a CSV requirement trigger matches the vessel configuration
 */
function checkCSVRequirementTrigger(row, vesselConfig) {
  const triggerType = row['Trigger Type'];
  const triggerCondition = row['Trigger Condition'];

  // All vessels requirements
  if (triggerType === 'ALL VESSELS' || triggerCondition === 'Always') {
    return true;
  }

  // Vessel type specific
  if (triggerType === 'PASSENGER') {
    const isPassenger = vesselConfig.vesselType === 'pax' || 
                        vesselConfig.passengerCapacity > 0;
    if (!isPassenger) return false;
    
    // Check passenger count conditions
    if (triggerCondition === '>12') {
      return vesselConfig.passengerCapacity > 12;
    } else if (triggerCondition === '<=12') {
      return vesselConfig.passengerCapacity <= 12;
    }
    return isPassenger;
  }

  if (triggerType === 'NON-PASSENGER') {
    return vesselConfig.vesselType !== 'pax' && 
           (!vesselConfig.passengerCapacity || vesselConfig.passengerCapacity === 0);
  }

  // Category based (inland waterways)
  if (triggerType === 'CATEGORY') {
    const category = triggerCondition.replace('CATEGORY,', '').trim();
    return vesselConfig.waterCategory === category;
  }

  // Length based
  if (triggerType === 'LENGTH') {
    const length = parseFloat(vesselConfig.lengthOverall) || 0;
    
    if (triggerCondition.includes('<')) {
      const value = parseFloat(triggerCondition.replace(/[<m]/g, ''));
      return length < value;
    } else if (triggerCondition.includes('>')) {
      const value = parseFloat(triggerCondition.replace(/[>m]/g, ''));
      return length > value;
    } else if (triggerCondition.includes('-')) {
      const [min, max] = triggerCondition.replace('m', '').split('-').map(v => parseFloat(v));
      return length >= min && length <= max;
    }
  }

  // Engine based
  if (triggerType === 'ENGINE') {
    if (triggerCondition.includes('kW')) {
      const power = parseFloat(vesselConfig.enginePower) || 0;
      const value = parseFloat(triggerCondition.replace(/[>kW]/g, ''));
      return power > value;
    }
    // Fuel type
    return vesselConfig.fuelType?.toLowerCase() === triggerCondition.toLowerCase();
  }

  // Manning based
  if (triggerType === 'MANNING') {
    if (triggerCondition === 'Single-handed') {
      return vesselConfig.crewCount === 1;
    } else if (triggerCondition === 'Multi-crew') {
      return vesselConfig.crewCount > 1;
    }
  }

  // Operations based
  if (triggerType === 'OPERATIONS') {
    return vesselConfig.operations?.includes(triggerCondition.toLowerCase().replace(' ', '_'));
  }

  // Season based
  if (triggerType === 'SEASON') {
    // This would need current season logic
    return false; // Placeholder
  }

  // Boatmaster requirements (inland)
  if (triggerType === 'BOATMASTER') {
    return vesselConfig.operatingStatus === 'inland';
  }

  return false;
}

// Existing hardcoded requirements for fallback
export const vesselRequirements = {
  // BASIC REQUIREMENTS - Apply to ALL vessels
  basicRequirements: {
    equipment: [
      // Communications
      {
        id: 'vhf_dsc',
        name: 'VHF Radio with DSC',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 17.3',
        category: 'Communications',
        priority: 'required'
      },
      
      // Life Saving Appliances (LSA)
      {
        id: 'lifejackets',
        name: 'Lifejackets for 110% persons',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.2',
        category: 'LSA',
        priority: 'required'
      },
      {
        id: 'lifebuoys',
        name: 'Lifebuoys',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.2.2',
        category: 'LSA',
        priority: 'required',
        note: 'Number depends on vessel length'
      },
      {
        id: 'thermal_protective_aids',
        name: 'Thermal Protective Aids',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.3',
        category: 'LSA',
        priority: 'required',
        note: '2 per liferaft'
      },
      {
        id: 'pyrotechnics',
        name: 'Pyrotechnics (Flares)',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.7',
        category: 'LSA',
        priority: 'required',
        note: 'Red hand flares, orange smoke, rocket parachute flares'
      },
      
      // Fire Fighting Equipment (FFE)
      {
        id: 'fire_extinguishers',
        name: 'Portable Fire Extinguishers',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 15.3',
        category: 'FFE',
        priority: 'required'
      },
      {
        id: 'fire_buckets',
        name: 'Fire Buckets with Lanyards',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 15.4',
        category: 'FFE',
        priority: 'required',
        note: '2 buckets minimum'
      },
      {
        id: 'fire_blanket',
        name: 'Fire Blanket',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 15.5',
        category: 'FFE',
        priority: 'required',
        note: 'Light duty in galley'
      },
      
      // Navigation Equipment
      {
        id: 'compass',
        name: 'Magnetic Compass',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 19.2',
        category: 'Navigation',
        priority: 'required'
      },
      {
        id: 'navigation_lights',
        name: 'Navigation Lights',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 19.8',
        category: 'Navigation',
        priority: 'required'
      },
      {
        id: 'sound_signals',
        name: 'Sound Signalling Equipment',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 19.9',
        category: 'Navigation',
        priority: 'required',
        note: 'Whistle/horn as per vessel size'
      },
      {
        id: 'shapes',
        name: 'Day Shapes',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 19.10',
        category: 'Navigation',
        priority: 'required',
        note: 'Ball, cone, diamond, cylinder'
      },
      
      // Medical
      {
        id: 'first_aid_kit',
        name: 'First Aid Kit',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 16.1',
        category: 'Medical',
        priority: 'required',
        note: 'Category depends on area of operation'
      },
      
      // Machinery/Bilge
      {
        id: 'bilge_pumping',
        name: 'Bilge Pumping Arrangements',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 10.4',
        category: 'Machinery',
        priority: 'required'
      },
      {
        id: 'engine_ventilation',
        name: 'Engine Space Ventilation',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 8.5',
        category: 'Machinery',
        priority: 'required'
      },
      
      // General Safety
      {
        id: 'means_of_reboarding',
        name: 'Means of Reboarding',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 6.2.5',
        category: 'Safety',
        priority: 'required',
        note: 'Ladder, bathing platform, or similar'
      },
      {
        id: 'safety_harnesses',
        name: 'Safety Harnesses',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 6.2.6',
        category: 'Safety',
        priority: 'required',
        note: 'For working on deck'
      },
      {
        id: 'knife',
        name: 'Knife on Lanyard',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.8',
        category: 'Safety',
        priority: 'required',
        note: 'Accessible from helm position'
      }
    ],
    certificates: [
      {
        id: 'eng1_medical',
        name: 'ENG1 Medical Certificate',
        trigger: { type: 'all', condition: 'always' },
        reference: 'MSN 1815',
        category: 'Medical',
        priority: 'required',
        note: 'All crew - valid 2 years'
      },
      {
        id: 'basic_safety_training',
        name: 'Basic Safety Training',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 5.2',
        category: 'Safety',
        priority: 'required',
        note: 'STCW Basic Safety Training or equivalent'
      },
      {
        id: 'workboat_coc',
        name: 'Certificate of Competency',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 5.3',
        category: 'Qualification',
        priority: 'required',
        note: 'Master must hold appropriate CoC for vessel/area'
      }
    ],
    documentation: [
      {
        id: 'sms',
        name: 'Safety Management System',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 2.4',
        category: 'SMS',
        priority: 'required'
      },
      {
        id: 'crew_list',
        name: 'Crew List',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 3.5',
        category: 'Records',
        priority: 'required'
      },
      {
        id: 'logbook',
        name: 'Official Logbook',
        trigger: { type: 'all', condition: 'always' },
        reference: 'MSN 1874',
        category: 'Records',
        priority: 'required'
      },
      {
        id: 'stability_info',
        name: 'Stability Information',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 12B',
        category: 'Technical',
        priority: 'required',
        note: 'Stability booklet or approved stability guidance'
      },
      {
        id: 'equipment_record',
        name: 'Equipment Maintenance Records',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 14.1.4',
        category: 'Maintenance',
        priority: 'required'
      },
      {
        id: 'drill_records',
        name: 'Drill & Training Records',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 21.3',
        category: 'Training',
        priority: 'required'
      },
      {
        id: 'risk_assessments',
        name: 'Risk Assessments',
        trigger: { type: 'all', condition: 'always' },
        reference: 'WBC3 Section 2.3',
        category: 'Safety',
        priority: 'required',
        note: 'Generic and task-specific risk assessments'
      },
      {
        id: 'marpol_declaration',
        name: 'MARPOL Declaration',
        trigger: { type: 'all', condition: 'always' },
        reference: 'MARPOL Annex V',
        category: 'Environmental',
        priority: 'required',
        note: 'Garbage management plan'
      }
    ]
  },

  riskAssessments: [
    {
      id: 'high_speed_ops',
      name: 'High-Speed Operations Risk Assessment',
      trigger: { type: 'speed', condition: '>=20 knots' },
      reference: 'WBC3 Section 25.4.2',
      description: 'Risk assessment for all high speed or planing mode operations',
      priority: 'required'
    },
    {
      id: 'police_ops',
      name: 'Police Operations Risk Assessment',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      description: 'Risk assessment for law enforcement and high-risk police operations',
      priority: 'required'
    },
    {
      id: 'towing_ops',
      name: 'Towing Operations Risk Assessment',
      trigger: { type: 'operations', condition: 'towing_operations' },
      reference: 'WBC3 Section 26.3',
      description: 'Risk assessment for commercial towing operations',
      priority: 'required'
    },
    {
      id: 'dg_ops',
      name: 'Dangerous Goods Operations Risk Assessment',
      trigger: { type: 'operations', condition: 'dangerous_goods' },
      reference: 'WBC3 Section 29',
      description: 'Risk assessment for stowage and segregation of dangerous goods',
      priority: 'required'
    }
  ],

  additionalEquipment: [
    // Police Boats (Annex 3)
    {
      id: 'immersion_suits_all_police',
      name: 'Immersion Suits for ALL persons',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      category: 'LSA',
      note: 'Police boats require immersion suits for all persons'
    },
    {
      id: 'white_flares_4_police',
      name: '4 White Illuminating Flares',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      category: 'LSA',
      note: 'For emergency rescues at night'
    },
    {
      id: 'propeller_guards_police',
      name: 'Propeller Guards',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      category: 'Safety',
      note: 'Or alternative protection for persons in water'
    },
    
    // Pilot Boats (Section 27)
    {
      id: 'searchlight_pilot',
      name: 'Searchlight',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 14.12',
      category: 'Navigation',
      note: 'Required to illuminate ship\'s side during pilot transfer',
      priority: 'required'
    },
    {
      id: 'immersion_suits_pilot',
      name: 'Immersion Suits for All Persons',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 14.5',
      category: 'LSA',
      note: 'Required for all persons on pilot boats',
      priority: 'required'
    },
    {
      id: 'line_throwing_pilot',
      name: 'Line Throwing Apparatus (Half Set)',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27',
      category: 'LSA',
      note: 'Half a set required for pilot boats',
      priority: 'required'
    },
    {
      id: 'stretcher_pilot',
      name: 'Compact Stretcher',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 23.1',
      category: 'Medical',
      note: 'Required for pilot boats',
      priority: 'required'
    },
    {
      id: 'mob_recovery_pilot',
      name: 'MOB Recovery Equipment',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27',
      category: 'Safety',
      note: 'Equipment for recovering persons from water',
      priority: 'required'
    },
    {
      id: 'white_flares_pilot',
      name: '4 White Illuminating Flares',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3',
      category: 'LSA',
      note: 'For emergency rescues at night',
      priority: 'required'
    },
    {
      id: 'propeller_guards_pilot',
      name: 'Propeller Guards',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3',
      category: 'Safety',
      note: 'Or alternative protection for persons in water',
      priority: 'required'
    },
    {
      id: 'buoyant_lifelines_pilot',
      name: '2 Buoyant Lifelines (18m)',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3',
      category: 'LSA',
      note: 'With quoit secured to one end',
      priority: 'required'
    },
    {
      id: 'red_rocket_flares_pilot',
      name: '6 Red Rocket Parachute Flares',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3 (14.1)',
      category: 'LSA',
      note: 'Standard distress signals for pilot boats',
      priority: 'required'
    },
    // High-Speed Vessels
    {
      id: 'inboard_seating',
      name: 'Individual Inboard Seating',
      trigger: { type: 'speed', condition: '>=20 knots' },
      reference: 'WBC3 Section 25.4.3',
      category: 'Safety',
      note: 'With lateral support, avoiding shock load areas'
    }
  ],

  trainingRequirements: [
    {
      id: 'enhanced_lsa_police',
      name: 'Enhanced LSA Familiarisation',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      category: 'Familiarisation',
      priority: 'critical'
    },
    {
      id: 'law_enforcement',
      name: 'Law Enforcement Procedures',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      category: 'Operational',
      priority: 'required'
    },
    {
      id: 'pilot_transfer',
      name: 'Pilot Transfer Procedures',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27',
      category: 'Operational',
      priority: 'required'
    },
    {
      id: 'pilot_boarding_visibility',
      name: 'Pilot Boarding Visible from Control Position',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3 (10.1.2)',
      category: 'Design',
      priority: 'required',
      note: 'Pilot boarding activities must be visible from helm'
    },
    {
      id: 'dg_familiarisation',
      name: 'Dangerous Goods Familiarisation',
      trigger: { type: 'operations', condition: 'dangerous_goods' },
      reference: 'WBC3 Section 29',
      category: 'Operational',
      priority: 'required'
    },
    {
      id: 'high_speed_safety',
      name: 'High-Speed Operations Safety',
      trigger: { type: 'speed', condition: '>=20 knots' },
      reference: 'WBC3 Section 25.4',
      category: 'Operational',
      priority: 'required'
    }
  ],

  operationalProcedures: [
    {
      id: 'seated_high_speed',
      name: 'All Persons Seated During High-Speed Operations',
      trigger: { type: 'speed', condition: '>=20 knots' },
      reference: 'WBC3 Section 25.4.4',
      category: 'Safety',
      priority: 'critical'
    },
    {
      id: 'no_gunwale_sitting',
      name: 'No Sitting on Gunwales or Tubes',
      trigger: { type: 'combined', conditions: [
        { type: 'speed', condition: '>=20 knots' },
        { type: 'type', condition: 'rib' }
      ]},
      reference: 'WBC3 Section 25.4.5',
      category: 'Safety',
      priority: 'critical'
    }
  ],

  certificates: [
    {
      id: 'police_boat_cert',
      name: 'Police Boat Certificate',
      code: 'PO',
      trigger: { type: 'type', condition: 'police' },
      reference: 'WBC3 Annex 3',
      description: 'Special certificate for law enforcement vessels',
      replaces: 'standard_workboat_cert',
      validityPeriod: 5 // years
    },
    {
      id: 'pilot_boat_cert',
      name: 'Pilot Boat Certificate',
      code: 'PB',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.1',
      description: 'Dedicated pilot boat certificate or Workboat Certificate with Pilot Boat Endorsement',
      replaces: 'standard_workboat_cert',
      validityPeriod: 5 // years
    },
    {
      id: 'dg_compliance',
      name: 'Document of Compliance for Dangerous Goods',
      trigger: { type: 'operations', condition: 'dangerous_goods' },
      reference: 'WBC3 Section 29.2',
      description: 'Required for vessels carrying dangerous goods',
      additional: true, // This is in addition to workboat cert
      validityPeriod: 5 // years
    },
    {
      id: 'standard_workboat_cert',
      name: 'Workboat Certificate',
      code: 'WB',
      trigger: { type: 'default', condition: 'all' },
      reference: 'WBC3 Section 3',
      description: 'Standard certificate for workboats under WBC3',
      validityPeriod: 5 // years
    }
  ],

  assessments: [
    {
      id: 'vibration_assessment',
      name: 'Whole Body Vibration Assessment',
      trigger: { type: 'speed', condition: '>=20 knots' },
      reference: 'WBC3 Section 25.4.1 / MGN 436',
      category: 'Health & Safety',
      priority: 'required'
    }
  ],

  restrictions: [
    {
      id: 'no_petrol_police',
      name: 'No Petrol Engines Permitted',
      trigger: { type: 'type', condition: ['police', 'pilot'] },
      reference: 'WBC3 Annex 3 / Section 27.2.3',
      category: 'Design',
      priority: 'critical'
    },
    {
      id: 'no_forward_access',
      name: 'No Forward-Facing Accessways',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3',
      category: 'Design',
      priority: 'critical'
    }
  ],

  documentation: [
    {
      id: 'stability_booklet',
      name: 'Stability Information Booklet',
      trigger: { type: 'type', condition: 'pilot' },
      reference: 'WBC3 Section 27.2.3 / 12B.6',
      category: 'Technical',
      priority: 'required'
    },
    {
      id: 'dg_manifest',
      name: 'Dangerous Goods Manifest',
      trigger: { type: 'operations', condition: 'dangerous_goods' },
      reference: 'WBC3 Section 29.3',
      category: 'Operational',
      priority: 'required'
    }
  ],

  areaCategoryEquipment: [
    // EPIRB Requirements
    {
      id: 'epirb_406',
      name: '406 MHz EPIRB with GPS',
      trigger: { type: 'areaCategory', condition: '0-2' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required',
      note: 'Float-free arrangement required'
    },
    // SART Requirements
    {
      id: 'sart_2_units',
      name: '2 x Search and Rescue Transponders (SART)',
      trigger: { type: 'areaCategory', condition: '0-1' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required'
    },
    {
      id: 'sart_1_unit',
      name: '1 x Search and Rescue Transponder (SART)',
      trigger: { type: 'areaCategory', condition: '2' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required'
    },
    // PLB Requirements
    {
      id: 'plb_crew',
      name: 'Personal Locator Beacon (PLB) for each crew member',
      trigger: { type: 'areaCategory', condition: '0-2' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required',
      note: 'Required when working on deck'
    },
    // Additional GMDSS
    {
      id: 'portable_vhf_2_units',
      name: '2 x Portable VHF Radios',
      trigger: { type: 'areaCategory', condition: '0-2' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required'
    },
    {
      id: 'portable_vhf_1_unit',
      name: '1 x Portable VHF Radio',
      trigger: { type: 'areaCategory', condition: '3-6' },
      reference: 'WBC3 Table 17.2.1',
      category: 'GMDSS',
      priority: 'required'
    }
  ],

  crewCertificationByArea: [
    {
      id: 'gmdss_goc',
      name: 'GMDSS General Operator Certificate (GOC)',
      trigger: { type: 'areaCategory', condition: '0-2' },
      reference: 'WBC3 Section 17.7',
      category: 'Radio',
      priority: 'required',
      note: 'At least one crew member must hold GOC'
    },
    {
      id: 'gmdss_roc',
      name: 'GMDSS Restricted Operator Certificate (ROC)',
      trigger: { type: 'areaCategory', condition: '3-6' },
      reference: 'WBC3 Section 17.7',
      category: 'Radio',
      priority: 'required',
      note: 'At least one crew member must hold ROC'
    },
    {
      id: 'basic_safety_training',
      name: 'Basic Safety Training (STCW)',
      trigger: { type: 'areaCategory', condition: '0-6' },
      reference: 'WBC3 Section 5',
      category: 'Safety',
      priority: 'required',
      note: 'All crew members'
    },
    {
      id: 'medical_first_aid',
      name: 'Medical First Aid Certificate',
      trigger: { type: 'areaCategory', condition: '0-2' },
      reference: 'WBC3 Section 16.3',
      category: 'Medical',
      priority: 'required',
      note: 'At least one crew member'
    },
    {
      id: 'elementary_first_aid',
      name: 'Elementary First Aid',
      trigger: { type: 'areaCategory', condition: '3-6' },
      reference: 'WBC3 Section 16.3',
      category: 'Medical',
      priority: 'required',
      note: 'At least one crew member'
    }
  ],

  // Additional requirements from WBC3 comprehensive analysis
  tonnageRequirements: [
    {
      id: 'ais_300gt',
      name: 'AIS (Automatic Identification System)',
      trigger: { type: 'tonnage', condition: '>=300' },
      reference: 'SOLAS V/19',
      category: 'Navigation',
      priority: 'required'
    }
  ],

  engineRequirements: [
    {
      id: 'fixed_fire_below',
      name: 'Fixed Fire Suppression System',
      trigger: { type: 'enginePosition', condition: 'inboard_below' },
      reference: 'WBC3 Table 16.1.1.1',
      category: 'FFE',
      priority: 'critical',
      note: 'MANDATORY for engines below deck'
    },
    {
      id: 'general_alarm_power',
      name: 'General Alarm System',
      trigger: { type: 'enginePower', condition: '>750' },
      reference: 'WBC3 Section 14.6',
      category: 'Safety',
      priority: 'required'
    }
  ],

  lengthRequirements: [
    {
      id: 'general_alarm_length',
      name: 'General Alarm System',
      trigger: { type: 'length', condition: '>=24' },
      reference: 'WBC3 Section 14.6',
      category: 'Safety',
      priority: 'required'
    }
  ],

  passengerRequirements: [
    {
      id: 'pa_system_pax',
      name: 'PA System (Public Address)',
      trigger: { type: 'type', condition: 'pax' },
      reference: 'WBC3 General Requirement',
      category: 'Communications',
      priority: 'required',
      note: 'For safety announcements to passengers'
    },
    {
      id: 'general_alarm_pax_area',
      name: 'General Alarm System',
      trigger: { 
        type: 'combined', 
        conditions: [
          { type: 'areaCategory', condition: '0-2' },
          { type: 'maxPersons', condition: '>=16' }
        ]
      },
      reference: 'WBC3 Section 14.6.1',
      category: 'Safety',
      priority: 'required',
      note: 'Required for area 0-2 with ≥16 persons'
    },
    {
      id: 'stability_booklet_pax',
      name: 'Stability Information Booklet',
      trigger: { type: 'maxPersons', condition: '>=16' },
      reference: 'WBC3 Section 12B.1',
      category: 'Documentation',
      priority: 'required'
    }
  ],

  medicalRequirements: [
    {
      id: 'medical_cat_a',
      name: 'Medical Stores Category A',
      trigger: { type: 'areaCategory', condition: '0' },
      reference: 'WBC3 Section 23.1.1',
      category: 'Medical',
      priority: 'required',
      note: 'Ocean-going medical stores'
    },
    {
      id: 'medical_cat_b', 
      name: 'Medical Stores Category B',
      trigger: { type: 'areaCategory', condition: '1' },
      reference: 'WBC3 Section 23.1.1',
      category: 'Medical',
      priority: 'required',
      note: 'Offshore medical stores'
    },
    {
      id: 'medical_cat_c',
      name: 'Medical Stores Category C',
      trigger: { type: 'areaCategory', condition: '2-6' },
      reference: 'WBC3 Section 23.1.1',
      category: 'Medical',
      priority: 'required',
      note: 'Coastal/inshore medical stores'
    }
  ],

  manningRequirements: [
    {
      id: 'second_person_cat6_3',
      name: 'Second Person - Experienced',
      trigger: { type: 'areaCategory', condition: '3-6' },
      reference: 'WBC3 Table A5.1',
      category: 'Manning',
      priority: 'required',
      note: 'Owner decides competence'
    },
    {
      id: 'second_person_cat2',
      name: 'Second Person - Experienced with vessel',
      trigger: { type: 'areaCategory', condition: '2' },
      reference: 'WBC3 Table A5.1',
      category: 'Manning',
      priority: 'required',
      note: 'Master decides competence'
    },
    {
      id: 'second_person_cat1',
      name: 'Second Person - Yachtmaster Coastal',
      trigger: { type: 'areaCategory', condition: '1' },
      reference: 'WBC3 Table A5.1',
      category: 'Manning',
      priority: 'required',
      note: 'Certificate required'
    },
    {
      id: 'second_person_cat0',
      name: 'Second Person - Yachtmaster Offshore',
      trigger: { type: 'areaCategory', condition: '0' },
      reference: 'WBC3 Table A5.1',
      category: 'Manning',
      priority: 'required',
      note: 'Higher certificate required'
    }
  ]
};

/**
 * Inland Waterways Requirements
 * Based on MSN 1837, DSM Code, and inland waterways regulations
 */
const inlandWaterwaysRequirements = {
  basicRequirements: {
    equipment: [
      {
        id: 'vhf_radio',
        name: 'VHF Radio',
        reference: 'MSN 1837 Section 6',
        category: 'Communications',
        priority: 'required'
      },
      {
        id: 'magnetic_compass',
        name: 'Magnetic Compass',
        reference: 'MSN 1837 Section 7',
        category: 'Navigation',
        priority: 'required'
      },
      {
        id: 'navigation_lights',
        name: 'Navigation Lights',
        reference: 'MSN 1837 Section 8',
        category: 'Navigation',
        priority: 'required',
        note: 'As per vessel size and type'
      },
      {
        id: 'sound_signals',
        name: 'Sound Signals',
        reference: 'MSN 1837 Section 8',
        category: 'Navigation',
        priority: 'required'
      },
      {
        id: 'lifejackets',
        name: 'Lifejackets 100%',
        reference: 'MSN 1837 Section 5.1',
        category: 'LSA',
        priority: 'required',
        note: 'One for each person on board'
      },
      {
        id: 'lifebuoys',
        name: 'Lifebuoys',
        reference: 'MSN 1837 Section 5.2',
        category: 'LSA',
        priority: 'required',
        note: 'Number based on vessel length'
      },
      {
        id: 'fire_extinguishers',
        name: 'Fire Extinguishers',
        reference: 'MSN 1837 Section 4',
        category: 'FFE',
        priority: 'required'
      },
      {
        id: 'first_aid_kit',
        name: 'First Aid Kit',
        reference: 'MSN 1837 Section 9',
        category: 'Medical',
        priority: 'required'
      },
      {
        id: 'bilge_pumping',
        name: 'Bilge Pumping',
        reference: 'MSN 1837 Section 3',
        category: 'Machinery',
        priority: 'required'
      }
    ],
    certificates: [
      {
        id: 'boatmasters_licence',
        name: 'Boatmaster\'s Licence',
        reference: 'MSN 1837 Section 2',
        category: 'Crew',
        priority: 'required',
        note: 'Required for master'
      }
    ],
    documentation: [
      {
        id: 'sms',
        name: 'Safety Management System',
        reference: 'DSM Code (for >12 pax)',
        category: 'SMS',
        priority: 'conditional',
        note: 'Required for vessels carrying >12 passengers'
      },
      {
        id: 'certificate_of_compliance',
        name: 'Certificate of Compliance',
        reference: 'MSN 1837',
        category: 'Vessel',
        priority: 'required'
      }
    ]
  },
  
  waterCategoryRequirements: {
    // Category A - Narrow rivers/canals
    A: [
      {
        id: 'basic_equipment_only',
        name: 'Basic Equipment Set',
        reference: 'MSN 1837 Category A',
        description: 'Minimum equipment for sheltered waters'
      }
    ],
    // Category B - Wider waters
    B: [
      {
        id: 'enhanced_navigation',
        name: 'Enhanced Navigation Equipment',
        reference: 'MSN 1837 Category B',
        description: 'Additional equipment for wider waters'
      }
    ],
    // Category C - Tidal waters <1.2m waves
    C: [
      {
        id: 'tidal_equipment',
        name: 'Tidal Waters Equipment',
        reference: 'MSN 1837 Category C',
        description: 'Equipment for tidal waters with waves <1.2m',
        items: ['Additional lifebuoys', 'Enhanced communications']
      }
    ],
    // Category D - Tidal waters <2.0m waves
    D: [
      {
        id: 'rough_water_equipment',
        name: 'Rough Water Equipment',
        reference: 'MSN 1837 Category D',
        description: 'Equipment for tidal waters with waves <2.0m',
        items: ['Liferafts', 'EPIRB', 'Additional safety equipment']
      }
    ]
  },

  // Passenger vessel specific (>12 passengers)
  dsmCodeRequirements: [
    {
      id: 'dsm_sms',
      name: 'Domestic Safety Management System',
      trigger: { type: 'passengers', condition: '>12' },
      reference: 'DSM Code Section 1',
      category: 'SMS',
      priority: 'required',
      description: 'Full SMS required for >12 passenger vessels'
    },
    {
      id: 'dsm_audit',
      name: 'Annual DSM Audit',
      trigger: { type: 'passengers', condition: '>12' },
      reference: 'DSM Code Section 3',
      category: 'Audit',
      priority: 'required'
    },
    {
      id: 'dsm_certificate',
      name: 'DSM Certificate',
      trigger: { type: 'passengers', condition: '>12' },
      reference: 'DSM Code Section 2',
      category: 'Certificate',
      priority: 'required'
    }
  ]
};

/**
 * Get inland waterways requirements for a vessel
 */
function getInlandWaterwaysRequirements(vesselConfig) {
  const requirements = {
    basicEquipment: [],
    basicCertificates: [],
    basicDocumentation: [],
    riskAssessments: [],
    equipment: [],
    training: [],
    procedures: [],
    certificates: [],
    assessments: [],
    restrictions: [],
    documentation: [],
    areaCategoryEquipment: [],
    crewCertificationByArea: []
  };

  // Add basic inland waterways requirements
  if (inlandWaterwaysRequirements.basicRequirements) {
    Object.keys(inlandWaterwaysRequirements.basicRequirements).forEach(subCategory => {
      const targetKey = 'basic' + subCategory.charAt(0).toUpperCase() + subCategory.slice(1);
      if (requirements[targetKey]) {
        requirements[targetKey] = inlandWaterwaysRequirements.basicRequirements[subCategory];
      }
    });
  }

  // Add water category specific requirements
  if (vesselConfig.waterCategory && inlandWaterwaysRequirements.waterCategoryRequirements[vesselConfig.waterCategory]) {
    const categoryReqs = inlandWaterwaysRequirements.waterCategoryRequirements[vesselConfig.waterCategory];
    requirements.areaCategoryEquipment.push(...categoryReqs);
  }

  // Add DSM Code requirements for passenger vessels >12
  if (vesselConfig.passengerCapacity > 12) {
    inlandWaterwaysRequirements.dsmCodeRequirements.forEach(req => {
      if (req.category === 'SMS') {
        requirements.documentation.push(req);
      } else if (req.category === 'Certificate') {
        requirements.certificates.push(req);
      } else if (req.category === 'Audit') {
        requirements.assessments.push(req);
      }
    });
  }

  return requirements;
}

/**
 * Get all requirements for a vessel configuration
 * Uses hardcoded business logic - NOT CSV files
 */
export function getVesselRequirements(vesselConfig) {
  // Get requirements from domain packs (same source as FFE, LSA modules use)
  const domainRequirements = getRequirementsFromDomains(vesselConfig);
  
  // Get requirements from old CSV data (for backward compatibility)
  const csvRequirements = getGeneratedVesselRequirements(vesselConfig);
  
  // Merge both sources, with domain packs taking priority
  const requirements = {
    basicEquipment: [...(domainRequirements.basicEquipment || []), ...(csvRequirements.basicEquipment || [])],
    basicCertificates: [...(domainRequirements.basicCertificates || []), ...(csvRequirements.basicCertificates || [])],
    basicDocumentation: [...(domainRequirements.basicDocumentation || []), ...(csvRequirements.basicDocumentation || [])],
    riskAssessments: [...(domainRequirements.riskAssessments || []), ...(csvRequirements.riskAssessments || [])],
    equipment: [...(domainRequirements.equipment || []), ...(csvRequirements.equipment || [])],
    training: [...(domainRequirements.training || []), ...(csvRequirements.training || [])],
    procedures: [...(domainRequirements.procedures || []), ...(csvRequirements.procedures || [])],
    certificates: [...(domainRequirements.certificates || []), ...(csvRequirements.certificates || [])],
    assessments: [...(domainRequirements.assessments || []), ...(csvRequirements.assessments || [])],
    restrictions: [...(domainRequirements.restrictions || []), ...(csvRequirements.restrictions || [])],
    documentation: [...(domainRequirements.documentation || []), ...(csvRequirements.documentation || [])],
    areaCategoryEquipment: [...(domainRequirements.areaCategoryEquipment || []), ...(csvRequirements.areaCategoryEquipment || [])],
    crewCertificationByArea: [...(domainRequirements.crewCertificationByArea || []), ...(csvRequirements.crewCertificationByArea || [])]
  };

  // Check if vessel is inland waterways - merge with existing inland logic if needed
  if (vesselConfig.operatingStatus === 'inland') {
    // Get additional inland requirements from existing logic
    const additionalInland = getInlandWaterwaysRequirements(vesselConfig);
    
    // Merge CSV data with any additional hardcoded requirements
    Object.keys(additionalInland).forEach(category => {
      if (additionalInland[category] && Array.isArray(additionalInland[category])) {
        // Merge without duplicates based on requirement ID
        const existingIds = requirements[category].map(r => r.id);
        const newItems = additionalInland[category].filter(r => !existingIds.includes(r.id));
        requirements[category] = [...requirements[category], ...newItems];
      }
    });
    
    return requirements;
  }

  // For seagoing vessels, merge CSV data with any additional hardcoded logic
  // The existing vesselRequirements logic can still add to what CSV provides
  if (vesselRequirements.basicRequirements) {
    Object.keys(vesselRequirements.basicRequirements).forEach(subCategory => {
      const targetKey = 'basic' + subCategory.charAt(0).toUpperCase() + subCategory.slice(1);
      if (requirements[targetKey] && vesselRequirements.basicRequirements[subCategory]) {
        // Merge without duplicates
        const existingIds = requirements[targetKey].map(r => r.id);
        const newItems = vesselRequirements.basicRequirements[subCategory].filter(r => !existingIds.includes(r.id));
        requirements[targetKey] = [...requirements[targetKey], ...newItems];
      }
    });
  }

  // Check each requirement category for conditional requirements
  Object.keys(vesselRequirements).forEach(category => {
    if (category === 'basicRequirements') return; // Skip basic requirements
    
    const categoryData = vesselRequirements[category];
    if (Array.isArray(categoryData)) {
      categoryData.forEach(req => {
        if (checkRequirementTrigger(req.trigger, vesselConfig)) {
          const targetCategory = mapCategory(category);
          requirements[targetCategory].push(req);
        }
      });
    }
  });

  return requirements;
}


/**
 * Check if a requirement trigger matches the vessel configuration
 */
function checkRequirementTrigger(trigger, vesselConfig) {
  if (!trigger) return false;

  switch (trigger.type) {
    case 'type':
      const types = Array.isArray(trigger.condition) ? trigger.condition : [trigger.condition];
      return types.includes(vesselConfig.vesselType);
    
    case 'speed':
      const maxSpeed = parseFloat(vesselConfig.maxSpeed) || 0;
      if (trigger.condition.startsWith('>=')) {
        const threshold = parseFloat(trigger.condition.replace('>=', ''));
        return maxSpeed >= threshold;
      }
      return false;
    
    case 'operations':
      return vesselConfig.operations?.includes(trigger.condition);
    
    case 'combined':
      // All conditions must be met
      return trigger.conditions.every(cond => 
        checkRequirementTrigger(cond, vesselConfig)
      );
    
    case 'areaCategory':
      const category = parseCategory(vesselConfig.category);
      if (trigger.condition.includes('-')) {
        const [min, max] = trigger.condition.split('-').map(n => parseInt(n));
        return category >= min && category <= max;
      }
      return category === parseInt(trigger.condition);
    
    case 'tonnage':
      const tonnage = parseFloat(vesselConfig.grossTonnage) || 0;
      if (trigger.condition.startsWith('>=')) {
        const threshold = parseFloat(trigger.condition.replace('>=', ''));
        return tonnage >= threshold;
      }
      if (trigger.condition.startsWith('>')) {
        const threshold = parseFloat(trigger.condition.replace('>', ''));
        return tonnage > threshold;
      }
      return false;
    
    case 'enginePower':
      const power = parseFloat(vesselConfig.enginePower) || 0;
      if (trigger.condition.startsWith('>')) {
        const threshold = parseFloat(trigger.condition.replace('>', ''));
        return power > threshold;
      }
      return false;
    
    case 'length':
      const length = parseFloat(vesselConfig.lengthOverall) || 0;
      if (trigger.condition.startsWith('>=')) {
        const threshold = parseFloat(trigger.condition.replace('>=', ''));
        return length >= threshold;
      }
      if (trigger.condition.startsWith('>')) {
        const threshold = parseFloat(trigger.condition.replace('>', ''));
        return length > threshold;
      }
      return false;
    
    case 'maxPersons':
      const persons = parseInt(vesselConfig.passengerCapacity) || 0;
      if (trigger.condition.startsWith('>=')) {
        const threshold = parseInt(trigger.condition.replace('>=', ''));
        return persons >= threshold;
      }
      return false;
    
    case 'enginePosition':
      return vesselConfig.enginePosition === trigger.condition;
    
    case 'fuelType':
      return vesselConfig.fuelType === trigger.condition;
    
    default:
      return false;
  }
}

/**
 * Map database categories to output categories
 */
function mapCategory(dbCategory) {
  const mapping = {
    'riskAssessments': 'riskAssessments',
    'additionalEquipment': 'equipment',
    'trainingRequirements': 'training',
    'operationalProcedures': 'procedures',
    'certificates': 'certificates',
    'assessments': 'assessments',
    'restrictions': 'restrictions',
    'documentation': 'documentation',
    'areaCategoryEquipment': 'areaCategoryEquipment',
    'crewCertificationByArea': 'crewCertificationByArea',
    'tonnageRequirements': 'equipment',
    'engineRequirements': 'equipment',
    'lengthRequirements': 'equipment',
    'passengerRequirements': 'procedures',
    'manningRequirements': 'certificates',
    'medicalRequirements': 'equipment'
  };
  return mapping[dbCategory] || dbCategory;
}

export default vesselRequirements;